import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../config/api';
import {
  COUNTRIES,
  getProvincesForCountry,
  hasProvinceDropdown,
} from '../../utils/countriesAndProvinces';
import './AdminPages.css';
import './AdminSubscribers.css';
import './AdminRegisterMember.css';
import { MEMBERSHIP_SEASON as CURRENT_SEASON } from '../../config/membershipSeason';

const civiliteMap = {
  Madame: 'Mme',
  Monsieur: 'M',
};

const PAYMENT_METHOD_KEYS = {
  'E-banking': 'ebanking',
  Twint: 'twint',
  'Carte Revolut': 'revolut',
  'Carte de crédit': 'credit',
};

function fallbackAmount(membershipType, paymentMethod, currency) {
  const prices = {
    base: {
      'E-banking': { CHF: 40, EUR: 40 },
      Twint: { CHF: 40 },
      'Carte Revolut': { CHF: 40, EUR: 40 },
      'Carte de crédit': { CHF: 40, EUR: 40 },
    },
    plus: {
      'E-banking': { CHF: 40, EUR: 40 },
      Twint: { CHF: 40 },
      'Carte Revolut': { CHF: 40, EUR: 40 },
      'Carte de crédit': { CHF: 40, EUR: 40 },
    },
    under14plus: {
      'E-banking': { CHF: 30, EUR: 30 },
      Twint: { CHF: 30 },
      'Carte Revolut': { CHF: 30, EUR: 30 },
      'Carte de crédit': { CHF: 30, EUR: 30 },
    },
    under5: {
      all: { CHF: 0, EUR: 0 },
    },
  };
  if (!membershipType || !paymentMethod) return null;
  if (membershipType === 'under5') {
    return prices.under5.all[currency] ?? 0;
  }
  return prices[membershipType]?.[paymentMethod]?.[currency] ?? 0;
}

function amountFromPricing(pricing, membershipType, paymentMethod, currency) {
  const pm = PAYMENT_METHOD_KEYS[paymentMethod];
  if (!pm || !pricing?.cotisation?.[pm]) return null;
  const row = pricing.cotisation[pm];
  const eur = currency === 'EUR';
  if (membershipType === 'under5') {
    const v = eur ? row.u5Eur : row.u5Chf;
    return v == null ? 0 : Number(v);
  }
  if (membershipType === 'under14plus') {
    const v = eur ? row.u14Eur : row.u14Chf;
    return v == null ? 0 : Number(v);
  }
  if (membershipType === 'base' || membershipType === 'plus') {
    const v = eur ? row.adultEur : row.adultChf;
    return v == null ? 0 : Number(v);
  }
  return null;
}

function membershipTypeToPaymentType(membershipType) {
  if (membershipType === 'base') return 'STANDARD';
  if (membershipType === 'plus') return 'PLUS';
  if (membershipType === 'under14plus') return 'Under14';
  if (membershipType === 'under5') return 'Under5';
  return 'STANDARD';
}

const IDENTITY_TYPES = [
  "Carte d'identité",
  "Carta d'Identità",
  'Passeport',
  'Passaporto',
];

const AdminRegisterMember = () => {
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState([]);

  const [formData, setFormData] = useState({
    civilite: 'Monsieur',
    firstName: '',
    lastName: '',
    sexe: 'Homme',
    dateOfBirth: '',
    phone: '',
    email: '',
    secondaryEmail: '',
    preferredLanguage: 'fr',
    season: CURRENT_SEASON,
    codiceFiscale: '',
    birthCountry: '',
    birthCity: '',
    birthCanton: '',
    street: '',
    streetNumber: '',
    city: '',
    canton: '',
    postalCode: '',
    country: 'Suisse',
    identityType: "Carte d'identité",
    identityNumber: '',
    identityExpiry: '',
    identityIssueDate: '',
    identityIssueCanton: '',
    identityIssueCity: '',
    hasTessera: false,
    tesseraNumber: '',
    tesseraExpiry: '',
    tesseraPIN: '',
    hasSubscription: false,
    tribune: '',
    settore: '',
    fila: '',
    posto: '',
    hasBlackWhite: false,
    blackWhiteNumber: '',
    blackWhiteExpiry: '',
    marketing: false,
    profiling: false,
    minorAuth: false,
    imageRights: false,
    termsAccepted: false,
    isMinor: false,
    parentEmail: '',
    parentPhone: '',
    parentFirstName: '',
    parentLastName: '',
    parentSexe: 'Homme',
    parentBirthDate: '',
    parentBirthCity: '',
    parentBirthCanton: '',
    parentBirthCountry: '',
    otherFanClub: 'non',
    hasUnsubscribed: false,
    includePayment: true,
    membershipType: '',
    paymentMethod: '',
    currency: 'CHF',
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/settings/pricing');
        if (!cancelled) setPricing(res.data);
      } catch {
        if (!cancelled) setPricing(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!formData.dateOfBirth) return;
    const birthDate = new Date(formData.dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    setFormData((prev) => ({ ...prev, isMinor: age < 18 }));
  }, [formData.dateOfBirth]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const calculatedAmount = () => {
    if (!formData.includePayment) return null;
    if (!formData.membershipType || !formData.paymentMethod) return null;
    const fromApi = amountFromPricing(
      pricing,
      formData.membershipType,
      formData.paymentMethod,
      formData.currency
    );
    if (fromApi != null && Number.isFinite(fromApi)) return fromApi;
    return fallbackAmount(formData.membershipType, formData.paymentMethod, formData.currency);
  };

  const validate = () => {
    const next = [];
    if (!formData.firstName.trim()) next.push('Prénom obligatoire');
    if (!formData.lastName.trim()) next.push('Nom obligatoire');
    if (!formData.dateOfBirth) next.push('Date de naissance obligatoire');
    if (!formData.phone.trim()) next.push('Téléphone obligatoire');
    if (!formData.email.trim()) next.push('Email obligatoire');
    if (!formData.birthCountry || !formData.birthCity) {
      next.push('Pays et ville de naissance obligatoires');
    }
    if (!formData.street.trim() || !formData.streetNumber.trim() || !formData.city.trim()) {
      next.push('Adresse : rue, numéro et ville obligatoires');
    }
    if (!formData.postalCode.trim()) next.push('Code postal obligatoire');
    if (
      !formData.identityNumber.trim() ||
      !formData.identityExpiry ||
      !formData.identityIssueDate ||
      !formData.identityIssueCanton.trim() ||
      !formData.identityIssueCity.trim()
    ) {
      next.push(
        'Pièce d’identité : numéro, échéance, date et lieu d’émission (canton/département, commune) obligatoires'
      );
    }
    if (formData.country === 'Italie' && !formData.codiceFiscale.trim()) {
      next.push('Codice fiscale obligatoire pour la résidence en Italie');
    }
    if (!formData.termsAccepted) next.push('Les conditions générales doivent être acceptées');

    if (formData.hasTessera) {
      if (!formData.tesseraNumber.trim()) next.push('Numéro Juventus Card obligatoire');
      if (!formData.tesseraExpiry) next.push('Échéance Juventus Card obligatoire');
      if (!formData.tesseraPIN.trim()) next.push('PIN Webticket obligatoire');
    }
    if (formData.hasSubscription) {
      if (!formData.tribune.trim() || !formData.settore.trim() || !formData.fila.trim() || !formData.posto.trim()) {
        next.push('Abonnement : tribune, settore, fila et posto obligatoires');
      }
    }
    if (formData.hasBlackWhite) {
      if (!formData.blackWhiteNumber.trim() || !formData.blackWhiteExpiry) {
        next.push('Carte Black & White : numéro et échéance obligatoires');
      }
    }

    if (formData.isMinor) {
      if (!formData.parentEmail.trim()) next.push('Email du parent obligatoire');
      if (!formData.parentPhone.trim()) next.push('Téléphone du parent obligatoire');
      if (!formData.parentFirstName.trim() || !formData.parentLastName.trim()) {
        next.push('Prénom et nom du parent obligatoires');
      }
    }

    if (formData.includePayment) {
      if (!formData.membershipType) next.push('Type de cotisation obligatoire');
      if (formData.membershipType !== 'under5' && !formData.paymentMethod) {
        next.push('Mode de paiement obligatoire');
      }
    }

    setErrors(next);
    return next.length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      window.scrollTo(0, 0);
      return;
    }

    const civilite =
      civiliteMap[formData.civilite] || formData.civilite;

    const secondary = formData.secondaryEmail.trim();
    const payload = {
      email: formData.email.trim(),
      civilite,
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      sexe: formData.sexe,
      dateOfBirth: formData.dateOfBirth,
      phone: formData.phone.trim(),
      preferredLanguage: formData.preferredLanguage,
      season: formData.season || undefined,
      birthPlace: {
        country: formData.birthCountry,
        city: formData.birthCity,
        canton: formData.birthCanton || undefined,
      },
      address: {
        street: formData.street.trim(),
        streetNumber: formData.streetNumber.trim(),
        city: formData.city.trim(),
        canton: formData.canton || undefined,
        postalCode: formData.postalCode.trim(),
        country: formData.country,
      },
      identity: {
        type: formData.identityType,
        number: formData.identityNumber.trim(),
        expiryDate: formData.identityExpiry,
        issueDate: formData.identityIssueDate,
        issueCanton: formData.identityIssueCanton.trim(),
        issueCity: formData.identityIssueCity.trim(),
      },
      codiceFiscale: formData.codiceFiscale.trim() || undefined,
      juventusFanCard: {
        has: formData.hasTessera,
        number: formData.hasTessera ? formData.tesseraNumber.trim() : '',
        expiryDate: formData.tesseraExpiry,
        webticketPIN: formData.tesseraPIN,
      },
      subscription: {
        has: formData.hasSubscription,
        tribune: formData.tribune,
        settore: formData.settore,
        fila: formData.fila,
        posto: formData.posto,
      },
      blackWhiteCard: {
        has: formData.hasBlackWhite,
        number: formData.blackWhiteNumber,
        expiryDate: formData.blackWhiteExpiry,
      },
      isMinor: formData.isMinor ? 'true' : 'false',
      parentInfo: formData.isMinor
        ? {
            email: formData.parentEmail.trim(),
            phone: formData.parentPhone.trim(),
            firstName: formData.parentFirstName.trim(),
            lastName: formData.parentLastName.trim(),
            sexe: formData.parentSexe,
            dateOfBirth: formData.parentBirthDate || undefined,
            birthCity: formData.parentBirthCity || undefined,
            birthCanton: formData.parentBirthCanton || undefined,
            birthCountry: formData.parentBirthCountry || undefined,
          }
        : undefined,
      consents: {
        marketing: formData.marketing,
        profiling: formData.profiling,
        minorAuthorization: formData.isMinor ? formData.minorAuth : false,
        imageRights: formData.imageRights,
        termsAccepted: formData.termsAccepted,
      },
      otherFanClub: {
        isMember: formData.otherFanClub === 'oui',
        hasUnsubscribed: formData.hasUnsubscribed,
      },
    };

    if (secondary) {
      payload.secondaryEmail = secondary.toLowerCase();
    }

    if (formData.includePayment && formData.membershipType) {
      const method =
        formData.membershipType === 'under5' ? 'E-banking' : formData.paymentMethod;
      const amt = calculatedAmount();
      payload.payment = {
        method,
        type: membershipTypeToPaymentType(formData.membershipType),
        amount: amt != null ? amt : 0,
        currency: formData.currency || 'CHF',
        status: 'pending',
        date: new Date().toISOString(),
      };
    }

    setLoading(true);
    setErrors([]);
    try {
      await api.post('/users/register-member', payload);
      alert('Membre créé avec succès. Il apparaît dans « Nouveaux membres » tant que son abonnement n’est pas validé.');
      setFormData((prev) => ({
        ...prev,
        firstName: '',
        lastName: '',
        dateOfBirth: '',
        phone: '',
        email: '',
        secondaryEmail: '',
        codiceFiscale: '',
        birthCity: '',
        street: '',
        streetNumber: '',
        city: '',
        postalCode: '',
        identityNumber: '',
        identityExpiry: '',
        tesseraNumber: '',
        tesseraExpiry: '',
        tesseraPIN: '',
        tribune: '',
        settore: '',
        fila: '',
        posto: '',
        blackWhiteNumber: '',
        blackWhiteExpiry: '',
        termsAccepted: false,
        parentEmail: '',
        parentPhone: '',
        parentFirstName: '',
        parentLastName: '',
        membershipType: '',
        paymentMethod: '',
      }));
      window.scrollTo(0, 0);
    } catch (err) {
      const data = err.response?.data;
      const list = [];
      if (data?.errors && Array.isArray(data.errors)) {
        for (const row of data.errors) {
          list.push(row.msg || row.message || JSON.stringify(row));
        }
      } else if (data?.error) {
        list.push(typeof data.error === 'string' ? data.error : data.message || 'Erreur serveur');
      } else {
        list.push(err.message || 'Erreur réseau');
      }
      setErrors(list);
      window.scrollTo(0, 0);
    } finally {
      setLoading(false);
    }
  };

  const birthProvinces = getProvincesForCountry(formData.birthCountry);
  const addrProvinces = getProvincesForCountry(formData.country);
  const parentBirthProvinces = getProvincesForCountry(formData.parentBirthCountry);

  return (
    <div className="admin-page">
      <div className="admin-container">
        <div className="admin-page-header">
          <div>
            <h1>Inscrire un membre</h1>
            <p style={{ margin: '0.5rem 0 0', color: '#555' }}>
              Création d’un compte utilisateur sans envoi de pièces jointes (admin uniquement).
            </p>
          </div>
          <Link to="/admin" className="btn-add" style={{ textDecoration: 'none' }}>
            ← Tableau de bord
          </Link>
        </div>

        <div className="admin-register-member-banner">
          Les copies de pièces d’identité, Juventus Card, Black &amp; White et parent ne sont pas
          exigées ici. Indiquez les informations textuelles ; vous pourrez compléter les fichiers
          ultérieurement si besoin.
        </div>

        {errors.length > 0 && (
          <div className="admin-register-errors">
            <strong>Veuillez corriger les erreurs suivantes :</strong>
            <ul>
              {errors.map((msg, i) => (
                <li key={i}>{msg}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="admin-table-container" style={{ padding: '1.5rem' }}>
          <form onSubmit={handleSubmit} className="admin-form admin-register-member-form">
            <section className="admin-register-section">
              <h2>Identité et connexion</h2>
              <div className="form-row-2">
                <div className="form-group">
                  <label>Civilité</label>
                  <select name="civilite" value={formData.civilite} onChange={handleChange}>
                    <option value="Monsieur">Monsieur</option>
                    <option value="Madame">Madame</option>
                    <option value="M">M</option>
                    <option value="Mme">Mme</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Sexe</label>
                  <select name="sexe" value={formData.sexe} onChange={handleChange}>
                    <option value="Homme">Homme</option>
                    <option value="Femme">Femme</option>
                  </select>
                </div>
              </div>
              <div className="form-row-2">
                <div className="form-group">
                  <label>Prénom</label>
                  <input name="firstName" value={formData.firstName} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Nom</label>
                  <input name="lastName" value={formData.lastName} onChange={handleChange} />
                </div>
              </div>
              <div className="form-row-2">
                <div className="form-group">
                  <label>Date de naissance</label>
                  <input
                    type="date"
                    name="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>Téléphone</label>
                  <input name="phone" value={formData.phone} onChange={handleChange} placeholder="+41..." />
                </div>
              </div>
              <div className="form-row-2">
                <div className="form-group">
                  <label>Email (connexion)</label>
                  <input type="email" name="email" value={formData.email} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Email secondaire (optionnel)</label>
                  <input
                    type="email"
                    name="secondaryEmail"
                    value={formData.secondaryEmail}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <p style={{ color: '#555', fontSize: '0.9rem', margin: '0 0 0.75rem' }}>
                Le mot de passe du compte est généré automatiquement côté serveur (aucune saisie ici).
              </p>
              <div className="form-row-2">
                <div className="form-group">
                  <label>Langue préférée</label>
                  <select name="preferredLanguage" value={formData.preferredLanguage} onChange={handleChange}>
                    <option value="fr">Français</option>
                    <option value="it">Italiano</option>
                  </select>
                </div>
              <div className="form-group">
                <label>Saison</label>
                <select name="season" value={formData.season} onChange={handleChange}>
                  <option value="">— Non renseigné —</option>
                  <option value={CURRENT_SEASON}>{CURRENT_SEASON}</option>
                  <option value="2024-2025">2024-2025</option>
                </select>
              </div>
              </div>
              <p style={{ color: '#555', fontSize: '0.95rem', marginTop: '0.5rem' }}>
                Statut mineur / majeur : déterminé automatiquement à partir de la date de naissance (
                {formData.dateOfBirth ? (formData.isMinor ? 'mineur' : 'majeur') : 'renseigner la date'})
              </p>
            </section>

            <section className="admin-register-section">
              <h2>Lieu de naissance</h2>
              <div className="form-row-2">
                <div className="form-group">
                  <label>Pays</label>
                  <select
                    name="birthCountry"
                    value={formData.birthCountry}
                    onChange={handleChange}
                  >
                    <option value="">—</option>
                    {COUNTRIES.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Ville</label>
                  <input name="birthCity" value={formData.birthCity} onChange={handleChange} />
                </div>
              </div>
              {hasProvinceDropdown(formData.birthCountry) && (
                <div className="form-group">
                  <label>Canton / Province</label>
                  <select
                    name="birthCanton"
                    value={formData.birthCanton}
                    onChange={handleChange}
                  >
                    <option value="">—</option>
                    {birthProvinces.map((p) => (
                      <option key={p.code} value={p.code}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </section>

            <section className="admin-register-section">
              <h2>Adresse</h2>
              <div className="form-row-2">
                <div className="form-group">
                  <label>Rue</label>
                  <input name="street" value={formData.street} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Numéro</label>
                  <input name="streetNumber" value={formData.streetNumber} onChange={handleChange} />
                </div>
              </div>
              <div className="form-row-2">
                <div className="form-group">
                  <label>Ville</label>
                  <input name="city" value={formData.city} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Code postal</label>
                  <input name="postalCode" value={formData.postalCode} onChange={handleChange} />
                </div>
              </div>
              <div className="form-row-2">
                <div className="form-group">
                  <label>Pays</label>
                  <select name="country" value={formData.country} onChange={handleChange}>
                    {COUNTRIES.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
                {hasProvinceDropdown(formData.country) && (
                  <div className="form-group">
                    <label>Canton</label>
                    <select name="canton" value={formData.canton} onChange={handleChange}>
                      <option value="">—</option>
                      {addrProvinces.map((p) => (
                        <option key={p.code} value={p.code}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              <div className="form-group">
                <label>Codice fiscale (si résidence Italie)</label>
                <input name="codiceFiscale" value={formData.codiceFiscale} onChange={handleChange} />
              </div>
            </section>

            <section className="admin-register-section">
              <h2>Pièce d’identité (sans fichier)</h2>
              <div className="form-row-2">
                <div className="form-group">
                  <label>Type</label>
                  <select name="identityType" value={formData.identityType} onChange={handleChange}>
                    {IDENTITY_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Numéro</label>
                  <input name="identityNumber" value={formData.identityNumber} onChange={handleChange} />
                </div>
              </div>
              <div className="form-row-2">
                <div className="form-group">
                  <label>Date d’échéance</label>
                  <input
                    type="date"
                    name="identityExpiry"
                    value={formData.identityExpiry}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>Date d’émission</label>
                  <input
                    type="date"
                    name="identityIssueDate"
                    value={formData.identityIssueDate}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="form-row-2">
                <div className="form-group">
                  <label>Canton/Département d’émission</label>
                  <input
                    name="identityIssueCanton"
                    value={formData.identityIssueCanton}
                    onChange={handleChange}
                    placeholder="VD, VS, GE, 75, MI…"
                  />
                </div>
                <div className="form-group">
                  <label>Commune/localité d’émission</label>
                  <input
                    name="identityIssueCity"
                    value={formData.identityIssueCity}
                    onChange={handleChange}
                    placeholder="Exemple : Lausanne"
                  />
                </div>
              </div>
            </section>

            <section className="admin-register-section">
              <h2>Juventus Card</h2>
              <div className="form-group checkbox">
                <label>
                  <input type="checkbox" name="hasTessera" checked={formData.hasTessera} onChange={handleChange} />
                  Possède une Juventus Card
                </label>
              </div>
              {formData.hasTessera && (
                <div className="form-row-2">
                  <div className="form-group">
                    <label>Numéro</label>
                    <input
                      type="text"
                      name="tesseraNumber"
                      maxLength={12}
                      autoComplete="off"
                      inputMode="numeric"
                      value={formData.tesseraNumber}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>Échéance</label>
                    <input type="date" name="tesseraExpiry" value={formData.tesseraExpiry} onChange={handleChange} />
                  </div>
                  <div className="form-group">
                    <label>PIN Webticket</label>
                    <input type="text" name="tesseraPIN" value={formData.tesseraPIN} onChange={handleChange} autoComplete="off" />
                  </div>
                </div>
              )}
            </section>

            <section className="admin-register-section">
              <h2>Abonnement stade</h2>
              <div className="form-group checkbox">
                <label>
                  <input
                    type="checkbox"
                    name="hasSubscription"
                    checked={formData.hasSubscription}
                    onChange={handleChange}
                  />
                  Abonnement au stade
                </label>
              </div>
              {formData.hasSubscription && (
                <div className="form-row-2">
                  <div className="form-group">
                    <label>Tribune</label>
                    <input name="tribune" value={formData.tribune} onChange={handleChange} />
                  </div>
                  <div className="form-group">
                    <label>Settore</label>
                    <input name="settore" value={formData.settore} onChange={handleChange} />
                  </div>
                  <div className="form-group">
                    <label>Fila</label>
                    <input name="fila" value={formData.fila} onChange={handleChange} />
                  </div>
                  <div className="form-group">
                    <label>Posto</label>
                    <input name="posto" value={formData.posto} onChange={handleChange} />
                  </div>
                </div>
              )}
            </section>

            <section className="admin-register-section">
              <h2>Carte Black &amp; White</h2>
              <div className="form-group checkbox">
                <label>
                  <input type="checkbox" name="hasBlackWhite" checked={formData.hasBlackWhite} onChange={handleChange} />
                  Possède une carte Black &amp; White / J1897
                </label>
              </div>
              {formData.hasBlackWhite && (
                <div className="form-row-2">
                  <div className="form-group">
                    <label>Numéro</label>
                    <input name="blackWhiteNumber" value={formData.blackWhiteNumber} onChange={handleChange} />
                  </div>
                  <div className="form-group">
                    <label>Échéance</label>
                    <input
                      type="date"
                      name="blackWhiteExpiry"
                      value={formData.blackWhiteExpiry}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              )}
            </section>

            {formData.isMinor && (
              <section className="admin-register-section">
                <h2>Représentant légal</h2>
                <div className="form-row-2">
                  <div className="form-group">
                    <label>Email parent</label>
                    <input type="email" name="parentEmail" value={formData.parentEmail} onChange={handleChange} />
                  </div>
                  <div className="form-group">
                    <label>Téléphone parent</label>
                    <input name="parentPhone" value={formData.parentPhone} onChange={handleChange} />
                  </div>
                </div>
                <div className="form-row-2">
                  <div className="form-group">
                    <label>Prénom</label>
                    <input name="parentFirstName" value={formData.parentFirstName} onChange={handleChange} />
                  </div>
                  <div className="form-group">
                    <label>Nom</label>
                    <input name="parentLastName" value={formData.parentLastName} onChange={handleChange} />
                  </div>
                </div>
                <div className="form-row-2">
                  <div className="form-group">
                    <label>Sexe</label>
                    <select name="parentSexe" value={formData.parentSexe} onChange={handleChange}>
                      <option value="Homme">Homme</option>
                      <option value="Femme">Femme</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Date de naissance parent</label>
                    <input
                      type="date"
                      name="parentBirthDate"
                      value={formData.parentBirthDate}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="form-row-2">
                  <div className="form-group">
                    <label>Ville de naissance</label>
                    <input name="parentBirthCity" value={formData.parentBirthCity} onChange={handleChange} />
                  </div>
                  <div className="form-group">
                    <label>Pays de naissance</label>
                    <select
                      name="parentBirthCountry"
                      value={formData.parentBirthCountry}
                      onChange={handleChange}
                    >
                      <option value="">—</option>
                      {COUNTRIES.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                {hasProvinceDropdown(formData.parentBirthCountry) && (
                  <div className="form-group">
                    <label>Canton / Province naissance parent</label>
                    <select name="parentBirthCanton" value={formData.parentBirthCanton} onChange={handleChange}>
                      <option value="">—</option>
                      {parentBirthProvinces.map((p) => (
                        <option key={p.code} value={p.code}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="form-group checkbox">
                  <label>
                    <input type="checkbox" name="minorAuth" checked={formData.minorAuth} onChange={handleChange} />
                    Autorisation parentale enregistrée
                  </label>
                </div>
              </section>
            )}

            <section className="admin-register-section">
              <h2>Autre fan club</h2>
              <div className="form-group">
                <label>Membre d’un autre fan club officiel ?</label>
                <select name="otherFanClub" value={formData.otherFanClub} onChange={handleChange}>
                  <option value="non">Non</option>
                  <option value="oui">Oui</option>
                </select>
              </div>
              <div className="form-group checkbox">
                <label>
                  <input
                    type="checkbox"
                    name="hasUnsubscribed"
                    checked={formData.hasUnsubscribed}
                    onChange={handleChange}
                  />
                  Désinscription / situation particulière déclarée
                </label>
              </div>
            </section>

            <section className="admin-register-section">
              <h2>Cotisation et paiement</h2>
              <div className="form-group checkbox">
                <label>
                  <input
                    type="checkbox"
                    name="includePayment"
                    checked={formData.includePayment}
                    onChange={handleChange}
                  />
                  Enregistrer un bloc paiement (cotisation en attente de validation)
                </label>
              </div>
              {formData.includePayment && (
                <>
                  <div className="form-group">
                    <label>Type de cotisation</label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                      {[
                        ['base', 'Adulte — Standard (JOFC Adult)'],
                        ['plus', 'Adulte — Plus'],
                        ['under14plus', 'Moins de 14 ans (JOFC Under14 + Juventus Card)'],
                        ['under5', 'Moins de 5 ans (gratuit)'],
                      ].map(([val, label]) => (
                        <label key={val} style={{ fontWeight: 400 }}>
                          <input
                            type="radio"
                            name="membershipType"
                            value={val}
                            checked={formData.membershipType === val}
                            onChange={handleChange}
                          />{' '}
                          {label}
                        </label>
                      ))}
                    </div>
                  </div>
                  {formData.membershipType !== 'under5' && (
                    <div className="form-row-2">
                      <div className="form-group">
                        <label>Mode de paiement</label>
                        <select name="paymentMethod" value={formData.paymentMethod} onChange={handleChange}>
                          <option value="">—</option>
                          <option value="E-banking">E-banking</option>
                          <option value="Twint">Twint</option>
                          <option value="Carte Revolut">Carte Revolut</option>
                          <option value="Carte de crédit">Carte de crédit</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Devise</label>
                        <select name="currency" value={formData.currency} onChange={handleChange}>
                          <option value="CHF">CHF</option>
                          <option value="EUR">EUR</option>
                        </select>
                      </div>
                    </div>
                  )}
                  {formData.membershipType === 'under5' && (
                    <p style={{ color: '#555', fontSize: '0.95rem' }}>
                      Cotisation gratuite : montant 0 CHF, mode « E-banking » appliqué par défaut pour la structure des données.
                    </p>
                  )}
                  {calculatedAmount() !== null && (
                    <p style={{ fontWeight: 600 }}>
                      Montant calculé : {calculatedAmount()} {formData.currency}
                    </p>
                  )}
                </>
              )}
            </section>

            <section className="admin-register-section">
              <h2>Consentements</h2>
              <div className="form-group checkbox">
                <label>
                  <input type="checkbox" name="marketing" checked={formData.marketing} onChange={handleChange} />
                  Marketing
                </label>
              </div>
              <div className="form-group checkbox">
                <label>
                  <input type="checkbox" name="profiling" checked={formData.profiling} onChange={handleChange} />
                  Profilage
                </label>
              </div>
              <div className="form-group checkbox">
                <label>
                  <input type="checkbox" name="imageRights" checked={formData.imageRights} onChange={handleChange} />
                  Droits à l’image
                </label>
              </div>
              <div className="form-group checkbox">
                <label>
                  <input
                    type="checkbox"
                    name="termsAccepted"
                    checked={formData.termsAccepted}
                    onChange={handleChange}
                  />
                  <strong>Acceptation des conditions générales (obligatoire)</strong>
                </label>
              </div>
            </section>

            <button type="submit" className="btn-add" disabled={loading}>
              {loading ? 'Création…' : 'Créer le membre'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminRegisterMember;
